// Carga e inicialización del SDK de JavaScript de Facebook, compartidas por los canales que lo
// usan (Facebook, Instagram y el alta de WhatsApp por Embedded Signup). Una sola etiqueta <script>
// y un solo FB.init por sesión: dos cargas vuelven a ejecutar el bootstrap del SDK y la segunda
// inicialización pisaría las opciones de la primera.

// Id histórico de la etiqueta del SDK; se conserva para adoptar la que ya hubiera insertado
// cualquier otra pantalla.
const SDK_SCRIPT_ID = 'facebook-jssdk';
const SDK_LOAD_ERROR = 'Facebook SDK failed to load';

// Locales del SDK soportados; el resto de idiomas caen a inglés.
const FACEBOOK_SDK_LOCALES = { es: 'es_LA', en: 'en_US' };

// Locale del SDK según el idioma del usuario ("es", "es_MX", "en"...): así el popup de Meta sale
// en el idioma de la interfaz en lugar de siempre en inglés.
export const facebookSdkLocale = locale => {
  const language = String(locale || '')
    .toLowerCase()
    .split(/[-_]/)[0];
  return FACEBOOK_SDK_LOCALES[language] || FACEBOOK_SDK_LOCALES.en;
};

// Carga en curso; se olvida si falla, para poder reintentar.
let sdkLoading = null;

// Olvida la carga fallida y quita la etiqueta para que el siguiente intento inserte una nueva.
const failSdkLoad = (script, reject) => {
  if (script) script.remove();
  sdkLoading = null;
  reject(new Error(SDK_LOAD_ERROR));
};

// Cada cuánto se comprueba si ya apareció window.FB, y cuántas veces, al adoptar una etiqueta
// que insertó otra pantalla (50 ms x 300 = 15 s).
const ADOPT_POLL_MS = 50;
const ADOPT_MAX_POLLS = 300;

// Espera a que termine de cargar una etiqueta que insertó otra pantalla. Se sondea window.FB en
// lugar de engancharse a window.fbAsyncInit, que el SDK dispara una sola vez y puede haber
// ocurrido ya; así nunca queda una promesa colgada.
const adoptExistingScript = (script, resolve, reject) => {
  let polls = 0;
  const timer = setInterval(() => {
    polls += 1;
    if (window.FB) {
      clearInterval(timer);
      resolve();
    } else if (polls >= ADOPT_MAX_POLLS) {
      clearInterval(timer);
      failSdkLoad(script, reject);
    }
  }, ADOPT_POLL_MS);
};

// Inserta la etiqueta del SDK con el locale pedido.
const insertScript = (locale, resolve, reject) => {
  const script = document.createElement('script');
  script.id = SDK_SCRIPT_ID;
  script.src = `https://connect.facebook.net/${facebookSdkLocale(
    locale
  )}/sdk.js`;
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.onload = () => {
    // Un onload sin window.FB es un script vacío (bloqueador de anuncios, proxy): es un fallo.
    if (window.FB) resolve();
    else failSdkLoad(script, reject);
  };
  script.onerror = () => failSdkLoad(script, reject);
  document.head.appendChild(script);
};

// Deja window.FB disponible. Si ya está cargado resuelve de inmediato; si otra pantalla insertó
// la etiqueta, espera a esa en lugar de insertar una segunda.
export const loadFacebookSdk = locale => {
  if (window.FB) return Promise.resolve();
  if (sdkLoading) return sdkLoading;

  sdkLoading = new Promise((resolve, reject) => {
    const existing = document.getElementById(SDK_SCRIPT_ID);
    if (existing) adoptExistingScript(existing, resolve, reject);
    else insertScript(locale, resolve, reject);
  });
  return sdkLoading;
};

// Versión de Graph por defecto si el entorno no la define; FB.init con version vacío falla.
const DEFAULT_API_VERSION = 'v23.0';

// Inicializa el SDK para la app de Meta una sola vez por sesión. Sin eventos automáticos ni
// escaneo de XFBML: ninguna pantalla los necesita y el primero manda actividad de la app a Meta.
export const initializeFacebook = (appId, apiVersion) => {
  if (window.fbSDKLoaded) return;

  window.FB.init({
    appId,
    autoLogAppEvents: false,
    xfbml: false,
    version: apiVersion || DEFAULT_API_VERSION,
    status: true,
  });
  window.fbSDKLoaded = true;
};

// Carga e inicializa el SDK con la configuración pública del entorno; idempotente.
export const setupFacebookSdk = async () => {
  const config = window.chatwootConfig || {};
  await loadFacebookSdk(config.selectedLocale);
  initializeFacebook(config.fbAppId, config.fbApiVersion);
};
